import React, { useEffect, useRef, useState } from "react";
import { useLang } from "../../i18n/LanguageProvider";

/**
 * Language switcher (replaces the old Google Translate widget).
 *
 * Renders the flag + native-name menu from the design (Dansk, Deutsch,
 * English, Español, Nederlands, Norsk, Svenska) and switches the app
 * language via the i18n LanguageProvider. The active language is
 * highlighted and persisted across reloads by the provider.
 *
 * The export is still named `Translator` so existing imports
 * (`import { Translator } from "./translate"`) keep working.
 */
export function Translator({ className = "" }) {
  const { lang, setLang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const active = langs.find((l) => l.code === lang) || langs[0];

  // Close when clicking outside.
  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        className="bg-white border border-gray-200 rounded-full px-3 py-2 shadow-sm flex items-center gap-2 hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg leading-none">{active.flag}</span>
        <span className="hidden sm:inline text-sm font-medium text-gray-800">
          {active.label}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 flex flex-col bg-white border border-gray-100 rounded-2xl shadow-lg w-48 p-1 z-50"
        >
          {langs.map((l) => {
            const isActive = l.code === lang;
            return (
              <li key={l.code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-600 font-semibold"
                      : "text-gray-800 hover:bg-gray-100"
                  }`}
                >
                  <span className="text-lg leading-none">{l.flag}</span>
                  <span className="text-sm">{l.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Translator;
